// Updated AuditLogPage.js
import React, { useState, useEffect } from 'react';
import apiClient from '../api';
import { formatDate } from '../utils/formatDate';

// Updated list including distinctive marks actions
const ACTION_TYPES = [
  'CREATE_JUVENILE',
  'UPDATE_JUVENILE',
  'CREATE_OFFENSE',
  'CREATE_DISTINCTIVE_MARK',
  'UPDATE_DISTINCTIVE_MARK',
  'DELETE_DISTINCTIVE_MARK',
  'CREATE_COURT_APPEARANCE',
  'UPDATE_COURT_APPEARANCE',
  'CREATE_PROBATION',
  'UPDATE_PROBATION',
  'CREATE_COMMUNITY_SERVICE',
  'UPDATE_COMMUNITY_SERVICE',
  'REGISTER_USER',
  'LOGIN_SUCCESS',
  'SECURITY_EVENT',
];

function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [filters, setFilters] = useState({ action_type: '' });
  const [appliedFilters, setAppliedFilters] = useState({ action_type: '' });
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const params = new URLSearchParams(appliedFilters).toString();
        const response = await apiClient.get(`/api/audit-logs?${params}`);
        setLogs(response.data);
      } catch (err) {
        setError('Failed to fetch audit logs.');
        console.error(err);
      }
    };
    fetchLogs();
  }, [appliedFilters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = (e) => {
    e.preventDefault();
    setAppliedFilters(filters);
  };

  if (error) return <div className="text-red-600 p-4">Error: {error}</div>;

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold text-slate-800 mb-6">Audit Log</h1>
      
      {/* Filter Section */}
      <div className="card mb-6">
        <form onSubmit={handleApplyFilters} className="flex gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-slate-700 mb-2">Filter by Action Type</label>
            <select
              name="action_type"
              value={filters.action_type}
              onChange={handleFilterChange}
              className="w-full"
            >
              <option value="" className="text-slate-500">-- All Action Types --</option>
              {ACTION_TYPES.map(type => (
                <option key={type} value={type} className="text-slate-900">{type.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </div>
          <button 
            type="submit"
            className="bg-yellow-500 text-white hover:bg-yellow-600 font-bold py-2 px-6 rounded-lg transition-colors"
          >
            Apply Filter
          </button>
        </form>
      </div>

      {/* Table Section */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left py-3 px-4 font-semibold text-slate-700">Timestamp</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-700">User</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-700">Action Type</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-700">Target ID</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-700">Details</th>
              </tr>
            </thead>
            <tbody>
              {logs.length > 0 ? (
                logs.map(log => (
                  <tr key={log.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 text-sm text-slate-600">
                      {formatDate(log.created_at)}
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-800 font-medium">
                      {log.full_name || 'System'}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                        {log.action_type.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-xs text-slate-500 font-mono">
                      {log.target_id}
                    </td>
                    <td className="py-3 px-4 text-xs text-slate-600 max-w-xs">
                      <pre className="whitespace-pre-wrap break-words">
                        {JSON.stringify(log.details, null, 2)}
                      </pre>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="py-8 px-4 text-center text-slate-500">
                    No audit logs found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default AuditLogPage;